/**
 * Feedback Master — read-only view of one definition.
 *
 * Shows the definition the way it will actually behave: the questions in display
 * order with their type-specific detail, the context allowlist, and who the
 * feedback routes to. A global default (appId = null) is labelled as such — it is
 * the platform-wide fallback, not this project's own row.
 */
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Tag } from 'sharedUi/Tag';
import { Button } from 'sharedUi/Button';
import { Message } from 'sharedUi/Message';
import DetailField from '../../components/common/detail-field';
import DetailSection from '../../components/common/detail-section';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import { feedbackMasterService } from '../../services/feedback-master.service';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useSelector } from 'react-redux';
import { PERMISSIONS } from '../../constants/permissions';
import { isAiComponentType } from '../feedback/feedback-scope';
import { useAiComponentTypes } from './use-feedback-value-sets';
import { useUserNames } from '../../hooks/useUserLookup';
import { isChoiceType, isRatingType } from './use-feedback-value-sets';
import { PageSkeleton } from '../../components/common/panel';

const FeedbackMasterView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useAppNavigate();
  const permKeys = useSelector((s: { auth?: { permissions?: string[] } }) => s.auth?.permissions || []);
  const canUpdate = new Set(permKeys).has(PERMISSIONS.FEEDBACK_MASTER_UPDATE);

  const q = useQuery({
    queryKey: ['feedback-master', id],
    queryFn: () => feedbackMasterService.get(id!, true),
    enabled: !!id,
  });
  const d = q.data;

  // Resolved before the early returns — hooks cannot run conditionally.
  const { nameOf } = useUserNames((d?.smeList ?? []).map((s) => s.userId));
  const aiComponentTypes = useAiComponentTypes();
  /* The screen that lists this definition, not the bare `/feedback-master`
     redirect — which always lands in the CORE module whatever the row is. */
  const listRoute = isAiComponentType(d?.component?.componentType, aiComponentTypes)
    ? '/ai-feedback?tab=definitions' : '/feedback?tab=definitions';

  if (q.isLoading) {
    return <PageSkeleton panels={2} />;
  }
  if (!d) {
    return (
      <div className="py-6 px-4 lg:px-6">
        <Message severity="warn" className="w-full justify-start" text="Feedback definition not found." />
      </div>
    );
  }

  return (
    <div className="py-4 px-4 lg:px-6" data-testid="SCR-FeedbackMaster-View">
      <div className="mb-4 flex items-center gap-3">
        <button aria-label="Back" type="button" onClick={() => navigate(listRoute)}
          className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 transition-colors">
          <i className="pi pi-arrow-left text-lg" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-ink">{d.name}</h1>
            <Tag value={d.status} severity={d.status === 'ACTIVE' ? 'success' : d.status === 'DRAFT' ? 'warning' : 'secondary'} />
            <Tag value={`v${d.definitionVersion}`} severity="info" />
            {!d.appId && <Tag value="Global default" severity="warning" />}
            {d.isDelete && <Tag value="DELETED" severity="danger" />}
          </div>
          <p className="font-mono text-xs text-ink-muted">{d.key}</p>
        </div>
        {canUpdate && !d.isDelete && (
          <Button label="Edit" icon="pi pi-pencil"
            onClick={() => navigate(`/feedback-master/${d._id}/edit`)} />
        )}
      </div>

      {d.versionNote && (
        <Message severity="info" className="mb-3 w-full justify-start"
          text={`v${d.definitionVersion}: ${d.versionNote}`} />
      )}

      <DetailSection title="Identity">
        <DetailField label="Description">{d.description || '—'}</DetailField>
        <DetailField label="Module">{d.module || '—'}</DetailField>
        <DetailField label="Area">{d.area || '—'}</DetailField>
      </DetailSection>

      <DetailSection title="What is being reviewed">
        <DetailField label="Component type">{d.component?.componentType || '—'}</DetailField>
        <DetailField label="Component code">{d.component?.componentCode || 'every component of this type'}</DetailField>
        <DetailField label="Component id hint">{d.component?.componentIdHint || '—'}</DetailField>
      </DetailSection>

      {/* Questions, in the order the widget renders them. */}
      <div className="mb-3 rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-3 text-sm font-semibold text-ink">
          Questions <span className="text-gray-400">· {d.questions?.length ?? 0}</span>
        </h3>
        {(d.questions ?? []).slice().sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)).map((qq, i) => (
          <div key={i} className="mb-2 rounded-md border border-gray-100 p-3 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs font-semibold text-ink">{qq.questionCode}</span>
              <Tag value={qq.questionType} severity="info" />
              {qq.required && <Tag value="required" severity="danger" />}
              {qq.signalRole && <Tag value={`signal: ${qq.signalRole}`} severity="success" />}
              {qq.allowComment && <Tag value="comment" severity="secondary" />}
              {isRatingType(qq.questionType) && qq.ratingScale && (
                <span className="text-xs text-gray-500">scale 1–{qq.ratingScale}</span>
              )}
            </div>
            <div className="mt-1 text-sm text-gray-800 dark:text-gray-200">{qq.label}</div>
            {qq.helpText && <div className="text-xs text-gray-500">{qq.helpText}</div>}
            {qq.visibleWhen?.questionCode && (
              <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                shown only when {qq.visibleWhen.questionCode} = {(qq.visibleWhen.equals ?? []).join(', ') || '—'}
              </div>
            )}
            {isChoiceType(qq.questionType) && (
              <div className="mt-2 flex flex-wrap gap-1">
                {qq.optionsSource
                  ? <span className="text-xs text-gray-500">options from value set “{qq.optionsSource}”</span>
                  : (qq.options ?? []).map((o, oi) => (
                    <span key={oi}
                      className={`rounded px-2 py-0.5 text-[11px] ${o.isActive === false
                        ? 'bg-gray-100 text-gray-400 line-through dark:bg-gray-700'
                        : 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'}`}
                      title={o.isActive === false ? 'Retired — old answers still readable' : undefined}>
                      {o.label} <span className="opacity-60">({o.value})</span>
                    </span>
                  ))}
              </div>
            )}
          </div>
        ))}
        {!d.questions?.length && <div className="text-sm text-gray-500">No questions.</div>}
      </div>

      {/* Context allowlist. */}
      <div className="mb-3 rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-1 text-sm font-semibold text-ink">
          Context capture <span className="text-gray-400">· {d.contextCapture?.length ?? 0}</span>
        </h3>
        <p className="mb-2 text-xs text-gray-500">Anything not listed here is dropped on submit.</p>
        {(d.contextCapture ?? []).map((c, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 border-b border-gray-100 py-1.5 last:border-0 dark:border-gray-700">
            <span className="font-mono text-xs font-semibold">{c.contextKey}</span>
            <span className="text-xs text-gray-400">←</span>
            <span className="font-mono text-xs text-gray-600 dark:text-gray-300">{c.sourcePath}</span>
            <Tag value={c.dataType} severity="secondary" />
            {c.required && <Tag value="required" severity="danger" />}
            {c.isSensitive && <Tag value="sensitive" severity="warning" />}
            {c.maxLength ? <span className="text-xs text-gray-400">max {c.maxLength}</span> : null}
          </div>
        ))}
        {!d.contextCapture?.length && <div className="text-sm text-gray-500">Nothing declared.</div>}
      </div>

      {/* Routing. */}
      <div className="mb-3 rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-2 text-sm font-semibold text-ink">
          SMEs <span className="text-gray-400">· who reviews it</span>
        </h3>
        {(d.smeList ?? []).map((s, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 border-b border-gray-100 py-1.5 last:border-0 dark:border-gray-700">
            {/* The id stays on the title so it is recoverable when two accounts
                share a display name, without putting an ObjectId on screen. */}
            <span className="text-sm font-medium text-ink" title={s.userId}>
              {nameOf(s.userId)}
            </span>
            {s.isPrimary && <Tag value="primary" severity="success" />}
            {s.role && <Tag value={s.role} severity="info" />}
            {s.notificationEnabled === false && <Tag value="not notified" severity="secondary" />}
          </div>
        ))}
        {!d.smeList?.length && (
          <div className="text-sm text-amber-600 dark:text-amber-400">
            Unrouted — feedback is captured but reaches nobody.
          </div>
        )}
      </div>

      <DetailSection title="Notification & retention">
        <DetailField label="Notify on submission">{d.notificationConfig?.notifyOnSubmission ? 'Yes' : 'No'}</DetailField>
        <DetailField label="Notify on action required">{d.notificationConfig?.notifyOnActionRequired ? 'Yes' : 'No'}</DetailField>
        <DetailField label="Notification code">{d.notificationConfig?.notificationCode || '—'}</DetailField>
        <DetailField label="Keep for">
          {d.retention?.retentionDays != null ? `${d.retention.retentionDays} days` : 'platform default'}
        </DetailField>
        <DetailField label="Anonymize after">
          {d.retention?.anonymizeAfterDays != null ? `${d.retention.anonymizeAfterDays} days` : 'never'}
        </DetailField>
      </DetailSection>

      <AuditInfoCard
        createdBy={d.createdBy}
        createdAt={d.createdAt}
        updatedBy={d.updatedBy}
        updatedAt={d.updatedAt}
      />
    </div>
  );
};

export default FeedbackMasterView;
